import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Link,
  TextField,
  useMediaQuery,
} from "@mui/material";
import DOMPurify from "dompurify";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer } from "react-leaflet";
import { useCurrentLocation } from "../../../hooks/useCurrentLocation";
import { createNewPlace } from "../../../services/placeService";
import { iresoftColors } from "../../../theme/ireColors";
import {
  checkNSFW,
  containsExplicitWords,
} from "../../../utils/explicitDetection";
import {
  fileToBase64,
  getCountryFromCoordinates,
  getRandomPhotoUrl,
} from "../../../utils/utils";
import AcceptanceCheckbox from "../AcceptanceCheckbox/AcceptanceCheckbox";
import FormSubmitButton from "../FormSubmitButton/FormSubmitButton";
import LocationField from "../LocationField/LocationField";
import LocationPicker from "../LocationPicker/LocationPicker";
import PhotoUploader from "../PhotoUploader/PhotoUploader";
import {
  defaultToastOptions,
  errorToast,
  successToast,
} from "../Toastify/Toastify";
import { useNavigate } from "react-router-dom";

interface AddPlaceFormProps {
  prefilledLocation?: { lat: number; lng: number; name: string } | null;
}

const AddPlaceForm: React.FC<AddPlaceFormProps> = ({ prefilledLocation }) => {
  const isSmallScreen = useMediaQuery("(max-width: 400px)");
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [useRandomPhoto, setUseRandomPhoto] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  const {
    location: currentLocation,
    loading: loadingLocation,
    getLocation,
    setLocationManually,
  } = useCurrentLocation(!!prefilledLocation);

  const [location, setLocation] = useState(
    prefilledLocation || currentLocation || null
  );
  const [tempLocation, setTempLocation] = useState(location);
  const [openMap, setOpenMap] = useState(false);
  const [acceptance, setAcceptance] = useState(false);

  const [randomPhotoUrl, setRandomPhotoUrl] = useState<string | undefined>();

  useEffect(() => {
    if (currentLocation) setLocation(currentLocation);
  }, [currentLocation]);
  useEffect(() => {
    if (openMap) setTempLocation(location);
  }, [openMap, location]);

  const handleRandomPhotoToggle = (checked: boolean) => {
    setUseRandomPhoto(checked);
    if (checked) {
      setPhoto(null);
      setRandomPhotoUrl(getRandomPhotoUrl());
    } else {
      setRandomPhotoUrl(undefined);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0] || null;
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      return errorToast(t("pages.currentlyReading.error.uploadOnlyImages"));
    }
    setPhoto(file);
    setUseRandomPhoto(false);
    setRandomPhotoUrl(undefined);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target?.files?.[0] || null;
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      return errorToast(t("pages.currentlyReading.error.uploadOnlyImages"));
    }
    setPhoto(file);
    setUseRandomPhoto(false);
    setRandomPhotoUrl(undefined);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!photo && !useRandomPhoto)
      return errorToast(t("pages.currentlyReading.error.uploadPhoto"));
    if (!location)
      return errorToast(t("pages.currentlyReading.error.selectLocation"));

    setIsSubmitting(true);

    let finalPhoto: File | null = photo;
    if (useRandomPhoto && randomPhotoUrl) {
      try {
        const response = await fetch(randomPhotoUrl);
        const blob = await response.blob();
        finalPhoto = new File([blob], `${crypto.randomUUID()}.jpg`, {
          type: blob.type,
        });
      } catch (err) {
        setIsSubmitting(false);
        return errorToast(
          t("pages.currentlyReading.error.uploadPhoto") + " - " + err
        );
      }
    }

    if (
      process.env.REACT_APP_EXPLICIT_DETECTION_IMG &&
      finalPhoto &&
      !useRandomPhoto
    ) {
      const isExplicit = await checkNSFW(finalPhoto);
      if (isExplicit) {
        setIsSubmitting(false);
        return errorToast(t("pages.currentlyReading.error.explicitPhoto"));
      }
    }

    if (process.env.REACT_APP_EXPLICIT_DETECTION_TEXT) {
      const checkExplicit = (value: string) => {
        const result = containsExplicitWords(value);
        if (result.found) {
          errorToast(
            `${t("pages.currentlyReading.error.explicitText")} (${result.word})`
          );
          return true;
        }
        return false;
      };
      if (checkExplicit(name) || checkExplicit(text)) {
        setIsSubmitting(false);
        return;
      }
    }

    if (location.name === "Unknown") {
      location.name = await getCountryFromCoordinates(
        location.lat,
        location.lng
      );
    }

    const base64Photo: string = await fileToBase64(finalPhoto!);
    const data = {
      title: DOMPurify.sanitize(name),
      description: DOMPurify.sanitize(text),
      imageBase64: base64Photo.replace(/^data:image\/[a-zA-Z]+;base64,/, ""),
      placeName: DOMPurify.sanitize(location.name),
      latitude: location.lat,
      longitude: location.lng,
    };

    try {
      await createNewPlace(data, t);
      successToast(t("pages.currentlyReading.success.submitted"));

      setName("");
      setText("");
      setPhoto(null);
      setUseRandomPhoto(false);
      setRandomPhotoUrl(undefined);
      setAcceptance(false);
      setLocation(prefilledLocation || null);
      setTempLocation(prefilledLocation || null);

      setTimeout(() => {
        navigate("/");
      }, defaultToastOptions.autoClose || 5000);
    } catch (error) {
      console.error(error);
      errorToast(t("api.error.post"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        minWidth: "375px",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        mt: { xs: 3, sm: 6 },
        boxSizing: "border-box",
        gap: 2,
        overflowY: "auto",
        mb: 8,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Link
          href="https://www.iresoft.cz/irebook"
          target="_blank"
          rel="noopener"
        >
          <Box
            component="img"
            src="imgs/irebook.png"
            alt="Irebook"
            sx={{
              width: isSmallScreen ? 85 : 175,
              height: isSmallScreen ? 85 : 175,
              objectFit: "contain",
              filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.5))",
              cursor: "pointer",
            }}
          />
        </Link>
      </Box>

      <Box
        sx={{
          width: isSmallScreen ? "90%" : "400px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <TextField
          label={t("pages.currentlyReading.form.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
          slotProps={{ input: { inputProps: { minLength: 3, maxLength: 20 } } }}
        />

        <LocationField
          location={location}
          getLocation={getLocation}
          setOpenMap={setOpenMap}
          loadingLocation={loadingLocation}
        />

        <TextField
          label={t("pages.currentlyReading.form.text")}
          value={text}
          onChange={(e) => {
            if (e.target.value.length <= 500) setText(e.target.value);
          }}
          multiline
          rows={4}
          fullWidth
          helperText={`${text.length}/500`}
          variant="outlined"
          InputLabelProps={{
            shrink: true,
          }}
          sx={{
            "& .MuiInputLabel-root": {
              transform: "translate(14px, 12px) scale(1)",
              transition: "all 0.2s ease",
            },
            "& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.MuiFormLabel-filled":
              {
                transform: "translate(14px, -9px) scale(0.75)",
              },

            "& .MuiOutlinedInput-notchedOutline": {
              legend: {
                width: 0,
              },
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline legend":
              {
                width: "auto",
              },

            "& .MuiOutlinedInput-root.MuiInputBase-formControl.Mui-focused:not(.Mui-error)":
              {},

            "& .MuiOutlinedInput-root.MuiFormControl-root .MuiOutlinedInput-notchedOutline legend":
              {},

            "& .MuiOutlinedInput-root.MuiInputBase-formControl.MuiInputBase-filled .MuiOutlinedInput-notchedOutline legend":
              {
                width: "auto",
              },
          }}
        />

        <PhotoUploader
          photo={photo}
          randomPhotoUrl={randomPhotoUrl}
          useRandomPhoto={useRandomPhoto}
          setPhoto={setPhoto}
          setUseRandomPhoto={handleRandomPhotoToggle}
          handleDrop={handleDrop}
          handleDragOver={handleDragOver}
          handleFileInputChange={handleFileInputChange}
        />

        <AcceptanceCheckbox
          acceptance={acceptance}
          setAcceptance={setAcceptance}
        />

        <FormSubmitButton isSubmitting={isSubmitting} />
      </Box>

      <Dialog
        open={openMap}
        onClose={() => setOpenMap(false)}
        fullWidth
        disableScrollLock
        slotProps={{ paper: { sx: { borderRadius: 2 } } }}
      >
        <DialogContent>
          <Box sx={{ height: 300, width: "100%" }}>
            <MapContainer
              center={[tempLocation?.lat || 50, tempLocation?.lng || 15]}
              zoom={13}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <LocationPicker
                location={tempLocation}
                setLocation={setTempLocation}
              />
            </MapContainer>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenMap(false)}
            variant="contained"
            sx={{ backgroundColor: iresoftColors.salmon100, color: "white" }}
          >
            {t("pages.currentlyReading.form.dialog.close")}
          </Button>
          <Button
            onClick={() => {
              if (tempLocation)
                setLocationManually(tempLocation.lat, tempLocation.lng);
              setOpenMap(false);
            }}
            variant="contained"
            sx={{ backgroundColor: iresoftColors.iresoftRed, color: "white" }}
          >
            {t("pages.currentlyReading.form.dialog.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddPlaceForm;
